// FormRenderer.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getContainerOrientation } from 'page/pageEditorLayoutUtils';
import { getNodeBlockKey } from 'page/pageEditorLayoutUtils';
import { getFieldLabel } from 'utilities/NodeLabels';
import { resolveDisplaySchemaFields } from 'views/utils/displaySchemaFields';

// Services
import { FORM_FIELD_BLOCK_TYPE, isFormExcludedFieldType } from 'services/forms/FormModel';

// Hooks
import useNodeFieldChange from 'node/form/hooks/useNodeFieldChange';
import useNodeFormFields from 'node/form/hooks/useNodeFormFields';
import useOverrideFields from 'node/form/hooks/useOverrideFields';

// CSS
import './FormRenderer.css';

// Components
import NodeField from 'node/form/field/NodeField';
import NodeFieldLabel from 'node/form/fieldlabel/NodeFieldLabel';

function normalizeString(value) {
  return String(value || '').trim();
}

function isEditableFieldBlock(block) {
  const blockType = normalizeString(block?.type).toUpperCase();
  return blockType === FORM_FIELD_BLOCK_TYPE || blockType === 'FIELDS';
}

/**
 * FormRenderer
 *
 * Renders an editable form layout for a node using FIELD blocks.
 */
const FormRenderer = React.memo(({
  formDoc = null,
  node = null,
  onChange,
  onCoverSaved,
  onLocationSaved,
  onSubtitleSaved,
  onTagsSaved,
  previewMode = false,
  typeDefOverride = null,
}) => {
  // ----- CONTEXT ----- //

  const language = useCurrentLanguage();
  const { theme } = useTheme();
  const { getType } = useNodeTypes();

  // ----- STATE ----- //

  const [hoveredFieldName, setHoveredFieldName] = useState('');

  // ----- MEMOS ----- //

  const getFieldWrapperProps = useCallback((fieldName) => ({
    style: {
      backgroundColor: hoveredFieldName === fieldName ? theme.backgroundHover : theme.backgroundHover,
      //borderColor: theme.outline,
    },
    onMouseEnter: () => setHoveredFieldName(fieldName),
    onMouseLeave: () => setHoveredFieldName(''),
  }), [hoveredFieldName, theme.backgroundHover, theme.panel]);

  const nodeTypeKey = String(node?.type || formDoc?.typeKey || '');
  const typeDef = typeDefOverride || getType(nodeTypeKey);

  // ----- HOOKS ----- //

  const { overrideFields } = useOverrideFields({
    nodeTypeKey,
    typeDef,
  });

  const { fields } = useNodeFormFields({
    language,
    nodeTypeKey,
    overrideFields,
    typeDef,
  });

  // ----- MEMOS ----- //

  const fieldsByName = useMemo(() => {
    const map = new Map();
    fields.forEach((field) => {
      const key = normalizeString(field?.name);
      if (key) map.set(key, field);
    });
    return map;
  }, [fields]);

  const schemaFieldByName = useMemo(() => {
    const map = new Map();
    resolveDisplaySchemaFields(typeDef, { includeHidden: false }).forEach((fieldDef) => {
      const key = normalizeString(fieldDef?.name);
      if (key) map.set(key, fieldDef);
    });
    return map;
  }, [typeDef]);

  // ----- HANDLERS ----- //

  const handleFieldChange = useNodeFieldChange({
    node,
    nodeTypeKey,
    onChange: previewMode ? undefined : onChange,
  });

  // ----- MEMOS ----- //

  const renderField = useCallback((field) => {
    if (!field) return null;

    return (
      <NodeField
        key={field.name}
        field={field}
        node={node}
        nodeTypeKey={nodeTypeKey}
        language={language}
        previewMode={previewMode}
        handleFieldChange={handleFieldChange}
        wrapperProps={getFieldWrapperProps(field.name)}
        onChange={previewMode ? undefined : onChange}
        onCoverSaved={previewMode ? undefined : onCoverSaved}
        onLocationSaved={previewMode ? undefined : onLocationSaved}
        onSubtitleSaved={previewMode ? undefined : onSubtitleSaved}
        onTagsSaved={previewMode ? undefined : onTagsSaved}
      />
    );
  }, [
    getFieldWrapperProps,
    handleFieldChange,
    language,
    node,
    nodeTypeKey,
    onChange,
    onCoverSaved,
    onSubtitleSaved,
    onLocationSaved,
    onTagsSaved,
    previewMode,
  ]);

  const renderFieldKey = useCallback((fieldKey) => {
    const cleanedFieldKey = normalizeString(fieldKey);
    if (!cleanedFieldKey) return null;

    const field = fieldsByName.get(cleanedFieldKey);
    if (field) {
      if (isFormExcludedFieldType(field.type)) return null;
      return renderField(field);
    }

    const schemaField = schemaFieldByName.get(cleanedFieldKey);
    if (!schemaField) {
      return null;
    }

    // Cover/icon etc. stay in chrome (Add Cover Photo), not form bodies.
    if (isFormExcludedFieldType(schemaField.type)) {
      return null;
    }

    return (
      <div
        className="form-renderer-field-fallback node-field"
        key={cleanedFieldKey}
        {...getFieldWrapperProps(cleanedFieldKey)}
      >
        <NodeFieldLabel
          field={schemaField}
          label={getFieldLabel(schemaField, language) || schemaField.label || cleanedFieldKey}
        />
      </div>
    );
  }, [fieldsByName, getFieldWrapperProps, language, renderField, schemaFieldByName]);

  const renderFieldBlock = useCallback((block) => {
    const titleOverride = normalizeString(block?.titleOverride);
    const fieldKeys = (Array.isArray(block?.fieldKeys) ? block.fieldKeys : [])
      .map((fieldKey) => normalizeString(fieldKey))
      .filter(Boolean);

    if (!fieldKeys.length) return null;

    return (
      <div className="form-renderer-block" key={block?.key || fieldKeys.join('-')}>

        {/* BLOCK TITLE */}
        {titleOverride ? (
          <div className="form-renderer-block-title">{titleOverride}</div>
        ) : null}

        {/* BLOCK FIELDS */}
        <div className="form-renderer-block-fields">
          {fieldKeys.map((fieldKey) => renderFieldKey(fieldKey))}
        </div>
      </div>
    );
  }, [renderFieldKey]);

  const renderLayoutNode = useCallback((nodeKey, layout, blocksByKey) => {
    const layoutNode = layout?.nodes?.[nodeKey];
    if (!layoutNode) return null;

    if (layoutNode.kind === 'block_slot') {
      const blockKey = getNodeBlockKey(layoutNode);
      const block = blockKey ? blocksByKey?.[blockKey] : null;
      if (!block || !isEditableFieldBlock(block)) return null;
      return renderFieldBlock(block);
    }

    const children = Array.isArray(layoutNode.children) ? layoutNode.children : [];
    const orientation = getContainerOrientation(layoutNode.kind);
    const className = orientation === 'horizontal'
      ? 'form-renderer-row'
      : 'form-renderer-stack';

    return (
      <div className={className} key={layoutNode.key}>
        {children.map((childKey) => renderLayoutNode(childKey, layout, blocksByKey))}
      </div>
    );
  }, [renderFieldBlock]);

  const layout = formDoc?.layout;
  const blocksByKey = formDoc?.blocksByKey || {};
  const rootNodeKey = normalizeString(layout?.rootNodeKey) || 'layout_root';
  const followSchemaOrder = Boolean(formDoc?.isDefault && !formDoc?.layoutEdited);

  // Unedited default Details forms track type schema order. Persisted layout can
  // lag behind field reorder until form sync runs, so render schema order live.
  if (followSchemaOrder || !layout?.nodes || !Object.keys(blocksByKey).length) {
    return (
      <div className="form-renderer-fallback">
        {fields.map((field) => renderField(field))}
      </div>
    );
  }

  // ----- RENDER ----- //

  return (
    <div className="form-renderer-root">
      {renderLayoutNode(rootNodeKey, layout, blocksByKey)}
    </div>
  );
});

// Dev Tools
FormRenderer.displayName = 'FormRenderer';

export default FormRenderer;
